import axios from "axios";
import { useEffect, useState } from "react";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { Link, useParams } from "react-router-dom";

import { ApiConnectionString } from "../../constants/ApiConntection";
import "./ResetPrivacyPinPage.css";

export default function ResetPrivacyPinPage() {
  const { token } = useParams();

  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [showPin, setShowPin] = useState(false);

  const [checking, setChecking] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState("");
  const [complete, setComplete] = useState(false);

  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  const authToken =
    user?.token ??
    user?.Token ??
    null;

  useEffect(() => {
    const checkToken = async () => {
      if (!token || !authToken) {
        setChecking(false);
        setValidToken(false);
        return;
      }

      try {
        const response = await axios.get(
          `${ApiConnectionString}/api/privacy-pin/reset/${token}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        setValidToken(response.data === true);
      } catch (error) {
        console.error(error);
        setValidToken(false);
      } finally {
        setChecking(false);
      }
    };

    checkToken();
  }, [token, authToken]);

  const handlePinChange = (setter) => (event) => {
    const value = event.target.value
      .replace(/[^0-9]/g, "")
      .slice(0, 4);

    setter(value);
  };

  const resetPin = async (event) => {
    event.preventDefault();

    setError("");

    if (!/^\d{4}$/.test(pin)) {
      setError("Please enter a 4-digit PIN.");
      return;
    }

    if (pin !== confirmPin) {
      setError("The PINs do not match.");
      return;
    }

    try {
      setIsLoading(true);

      await axios.put(
        `${ApiConnectionString}/api/privacy-pin/reset`,
        {
          token,
          pin,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setComplete(true);
    } catch (error) {
      console.error(error);

      const message =
        typeof error.response?.data === "string"
          ? error.response.data
          : error.response?.data?.message ??
            error.response?.data?.Message ??
            "Your PIN could not be reset.";

      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="reset-pin-page">
        <div className="reset-pin-card">
          <h1>Reset privacy PIN</h1>
          <p>Checking your reset link...</p>
        </div>
      </div>
    );
  }

  if (!authToken) {
    return (
      <div className="reset-pin-page">
        <div className="reset-pin-card">
          <h1>Sign in required</h1>

          <p>
            Please sign in to your Therapy Diary account before
            using your PIN reset link.
          </p>

          <Link to="/login" className="reset-pin-primary-btn">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  if (!validToken) {
    return (
      <div className="reset-pin-page">
        <div className="reset-pin-card">
          <h1>Reset link unavailable</h1>

          <p>
            This PIN reset link is invalid or has expired.
            Please request a new reset link from your account.
          </p>

          <Link to="/" className="reset-pin-primary-btn">
            Return to Therapy Diary
          </Link>
        </div>
      </div>
    );
  }

  if (complete) {
    return (
      <div className="reset-pin-page">
        <div className="reset-pin-card">
          <h1>PIN reset</h1>

          <p>
            Your privacy PIN has been successfully changed.
          </p>

          <Link to="/" className="reset-pin-primary-btn">
            Return to Therapy Diary
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-pin-page">
      <div className="reset-pin-card">
        <div className="reset-pin-heading">
          <h1>Reset privacy PIN</h1>

          <p>
            Choose a new 4-digit PIN to protect sensitive
            client details.
          </p>
        </div>

        {error && (
          <div className="reset-pin-error">
            {error}
          </div>
        )}

        <form onSubmit={resetPin}>
          <div className="reset-pin-field">
            <label htmlFor="new-pin">
              New PIN
            </label>

            <div className="reset-pin-input-wrapper">
              <input
                id="new-pin"
                type={showPin ? "text" : "password"}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                autoComplete="off"
                value={pin}
                onChange={handlePinChange(setPin)}
                placeholder="••••"
                autoFocus
              />

              <button
                type="button"
                className="reset-pin-eye"
                onClick={() =>
                  setShowPin((current) => !current)
                }
                aria-label={
                  showPin ? "Hide PIN" : "Show PIN"
                }
              >
                {showPin ? <EyeSlash /> : <Eye />}
              </button>
            </div>
          </div>

          <div className="reset-pin-field">
            <label htmlFor="confirm-pin">
              Confirm new PIN
            </label>

            <input
              id="confirm-pin"
              type={showPin ? "text" : "password"}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              autoComplete="off"
              value={confirmPin}
              onChange={handlePinChange(setConfirmPin)}
              placeholder="••••"
            />
          </div>

          <button
            type="submit"
            className="reset-pin-primary-btn"
            disabled={
              isLoading ||
              pin.length !== 4 ||
              confirmPin.length !== 4
            }
          >
            {isLoading
              ? "Resetting PIN..."
              : "Reset PIN"}
          </button>
        </form>
      </div>
    </div>
  );
}
